import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToastContext, Button, Input } from '@resideo/blueprint-oneportal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import apiService, { DeviceFilter } from '@/services/apiService';
import type {
  InviteUserRequest,
  InviteDataInfo,
  ConsumerUser,
  ConsumerUserWithLocations,
  ConsumerLocationDetail,
  ConsumerDeviceItem,
} from '@/services/apiService';
import { useAuth } from '@/contexts/AuthContext';
import styles from './InviteEmployee.module.css';
import { StepLocation } from './InviteEmployeeStepLocation';
import type { LocationRow } from './InviteEmployeeStepLocation';
import { RoleDropdown } from './RoleDropdown';
import { StepReview } from './InviteEmployeeStepReview';

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3;

// ─── Discard Draft Modal ──────────────────────────────────────────────────────

function DiscardDraftModal({ onDiscard, onStay }: { onDiscard: () => void; onStay: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={styles.discardOverlay}>
      <div className={styles.discardModal}>
        <p className={styles.discardTitle}>{t('inviteEmployee.discardTitle')}</p>
        <p className={styles.discardBody}>{t('inviteEmployee.discardBody')}</p>
        <div className={styles.discardActions}>
          <Button variant="highlightTertiary" onClick={onDiscard} style={{ fontWeight: 700 }}>
            {t('inviteEmployee.discardConfirm')}
          </Button>
          <Button variant="primary" onClick={onStay} style={{ fontWeight: 700 }}>
            {t('inviteEmployee.discardStay')}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = [
  { number: 1, label: 'inviteEmployee.stepEmployee' },
  { number: 2, label: 'inviteEmployee.stepLocation' },
  { number: 3, label: 'inviteEmployee.stepReview' },
];

// ─── Stepper ──────────────────────────────────────────────────────────────────

interface StepperProps {
  currentStep: Step;
}

function Stepper({ currentStep }: StepperProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.stepperWrapper}>
      <div className={styles.stepper}>
        {STEPS.map((step, index) => {
          const isActive = step.number === currentStep;
          const isCompleted = step.number < currentStep;
          return (
            <Fragment key={step.number}>
              <div className={styles.stepItem}>
                <div
                  className={[
                    styles.stepCircle,
                    isActive ? styles.stepCircleActive : '',
                    isCompleted ? styles.stepCircleCompleted : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {isCompleted ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M2.5 7L5.5 10L11.5 4"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    step.number
                  )}
                </div>
                <span
                  className={[
                    styles.stepLabel,
                    isActive ? styles.stepLabelActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {t(step.label)}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  key={`connector-${step.number}`}
                  className={[
                    styles.stepConnector,
                    isCompleted ? styles.stepConnectorCompleted : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 1: Employee details form ───────────────────────────────────────────

interface StepEmployeeProps {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onRoleChange: (v: string) => void;
  emailError?: string | null;
}

function StepEmployee({
  firstName,
  lastName,
  email,
  role,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onRoleChange,
  emailError,
}: StepEmployeeProps) {
  const { t } = useTranslation();
  return (
    <>
      <h2 className={styles.formTitle}>{t('inviteEmployee.stepEmployee')}</h2>
      <p className={styles.formDescription}>{t('inviteEmployee.stepEmployeeDescription')}</p>
      <div className={styles.formCard}>
        <form className={styles.form} onSubmit={e => e.preventDefault()}>
        {/* Row 1: First Name | Last Name */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="invite-first-name" className={styles.label}>
              {t('inviteEmployee.firstNameLabel')}
              <span className={styles.required}>*</span>
            </label>
            <Input
              id="invite-first-name"
              name="invite-first-name"
              type="text"
              placeholder={t('inviteEmployee.firstNamePlaceholder')}
              value={firstName}
              onChange={e => onFirstNameChange(e.target.value)}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="invite-last-name" className={styles.label}>
              {t('inviteEmployee.lastNameLabel')}
              <span className={styles.required}>*</span>
            </label>
            <Input
              id="invite-last-name"
              name="invite-last-name"
              type="text"
              placeholder={t('inviteEmployee.lastNamePlaceholder')}
              value={lastName}
              onChange={e => onLastNameChange(e.target.value)}
            />
          </div>
        </div>

        {/* Row 2: Email */}
        <div className={styles.formGroup}>
          <label htmlFor="invite-email" className={styles.label}>
            {t('inviteEmployee.emailLabel')}
            <span className={styles.required}>*</span>
          </label>
          <Input
            id="invite-email"
            name="invite-email"
            type="email"
            placeholder={t('inviteEmployee.emailPlaceholder')}
            value={email}
            onChange={e => onEmailChange(e.target.value)}
            error={emailError ?? undefined}
          />
        </div>

        {/* Row 3: Role */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            {t('inviteEmployee.roleLabel')}
            <span className={styles.required}>*</span>
          </label>
          <RoleDropdown value={role} onChange={onRoleChange} />
        </div>
      </form>
    </div>
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function InviteEmployee() {
  const navigate = useNavigate();
  const { addToast } = useToastContext();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');

  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set());
  const [cameraSelections, setCameraSelections] = useState<Map<string, Set<string>>>(new Map());
  const [camerasOn, setCamerasOn] = useState<Set<string>>(new Set());
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'loading'>('idle');
  const [locationsInitialised, setLocationsInitialised] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  // ─── consumerAccountId from AuthContext (no extra API call needed) ──────────
  const { locations: contextLocations, selectedLocation } = useAuth();
  const consumerAccountId: string | undefined =
    selectedLocation?.consumerAccountId ??
    contextLocations[0]?.consumerAccountId ??
    undefined;

  // ─── Accounts data with full device details (needed for Location step) ───────
  // useQuery deduplicates the request so React StrictMode's double-mount
  // never fires two network calls.
  const {
    data: accountsData,
    isFetching: isLoadingLocations,
    error: accountsError,
  } = useQuery({
    queryKey: ['consumerAccounts', 'ALL'],
    queryFn: () => apiService.getConsumerAccounts(undefined, DeviceFilter.ALL),
    refetchOnWindowFocus: false,
  });

  const locationsError = accountsError ? 'Unable to load locations. Please try again.' : null;

  // ─── Build LocationRow list from accounts data ───────────────────────────────
  const locations = useMemo<LocationRow[]>(() => {
    const allConsumerUsers: ConsumerUserWithLocations[] = accountsData?.data?.consumerUsers ?? [];
    // Only include locations that belong to the current consumerAccountId so that
    // accounts the user belongs to under a different consumerAccountId are excluded.
    const rawLocations: ConsumerLocationDetail[] = allConsumerUsers
      .filter(cu => !consumerAccountId || cu?.consumerAccount?.id === consumerAccountId)
      .flatMap(cu => cu?.consumerAccount?.locations ?? []);
    const filterByFamily = (devices: ConsumerDeviceItem[], family: string) =>
      devices
        .filter(d => d.device?.product?.productFamily === family)
        .map(d => ({ id: d.device?.deviceId ?? d.id ?? '', name: d.name ?? null }));

    return rawLocations.map(loc => {
      const addr = loc.address ?? {};
      const parts = [
        addr.addressLine1, addr.city,
        addr.stateProvinceRegionCode, addr.zipPostalCode,
      ].filter(Boolean);
      const allDevices: ConsumerDeviceItem[] = loc.consumerDevices ?? [];
      return {
        id: loc.id ?? '',
        name: loc.name ?? 'Unknown Location',
        fullAddress: parts.join(', '),
        devices: filterByFamily(allDevices, 'Camera'),
        thermostatDevices: filterByFamily(allDevices, 'Thermostat'),
        leakDetectorDevices: filterByFamily(allDevices, 'LeakDetector'),
        lockDevices: [],          // placeholder — productFamily TBD
        securityPanelDevices: [], // placeholder — partitions API TBD
      };
    });
  }, [accountsData]);

  // Initialise selection state once when locations first load
  useEffect(() => {
    if (locationsInitialised || locations.length === 0) return;
    setCamerasOn(new Set(locations.map(l => l.id)));
    const selections = new Map<string, Set<string>>();
    locations.forEach(l => selections.set(l.id, new Set(l.devices.map(d => d.id))));
    setCameraSelections(selections);
    setSelectedLocationIds(new Set(locations.map(l => l.id)));
    setLocationsInitialised(true);
  }, [locations, locationsInitialised]);

  // ─── Existing emails — read from React Query cache populated by Employees.tsx ─
  // No network call: if the cache is warm (user navigated from Employees list)
  // we get instant duplicate-email detection. If cold, Set stays empty and
  // the duplicate check is silently skipped (non-critical).
  const queryClient = useQueryClient();
  const existingEmails = useMemo<Set<string>>(() => {
    const cached = queryClient.getQueryData<ConsumerUser[]>(['consumerUsers', consumerAccountId, '']);
    if (!cached) return new Set();
    const emails = new Set<string>();
    cached.forEach((u: ConsumerUser) => {
      if (u.user?.contactEmail) emails.add(u.user.contactEmail.toLowerCase());
      (u.invites ?? []).forEach(inv => {
        if (inv.sendToEmail) emails.add(inv.sendToEmail.toLowerCase());
      });
    });
    return emails;
  }, [queryClient, consumerAccountId]);

  const handleToggleLocation = useCallback((id: string) => {
    setSelectedLocationIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailTrimmed = email.trim().toLowerCase();
  const isDuplicateEmail = EMAIL_REGEX.test(emailTrimmed) && existingEmails.has(emailTrimmed);
  const emailError = isDuplicateEmail ? 'User is already invited.' : null;
  const isStepOneValid = firstName.trim().length > 0 && lastName.trim().length > 0 && EMAIL_REGEX.test(emailTrimmed) && role.length > 0 && !isDuplicateEmail;

  const isStepTwoValid =
    role === 'ADMIN'
      ? true
      : selectedLocationIds.size > 0 &&
      [...selectedLocationIds].some(locId => (cameraSelections.get(locId)?.size ?? 0) > 0);

  function buildInviteBody(): InviteUserRequest {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
    const startDate = now.toISOString();
    const endDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();

    // Collect all selected device IDs across every selected location
    const deviceIds = [...selectedLocationIds].flatMap(locId =>
      [...(cameraSelections.get(locId) ?? new Set<string>())]
    );

    const inviteDataInfo: InviteDataInfo = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      role,
      sendToEmail: emailTrimmed,
      ...(role !== 'ADMIN' && { deviceIds }),
      ...(role === 'GUEST' && {
        scheduleInfo: {
          weekDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          startDate,
          endDate,
          startTime: '00:01',
          endTime: '23:59',
        },
      }),
    };

    return {
      InviteDataInfo: inviteDataInfo,
      emailTemplateInfo: {
        templateLanguage: 'en-US',
        baseUrl: `${import.meta.env.VITE_RESIDEO_ID_URL}/mobile`,
        expiresAt,
      },
    };
  }

  async function handleSaveAndInvite() {
    if (!consumerAccountId) return;
    setInviteStatus('loading');
    try {
      await apiService.sendInviteV2(consumerAccountId, buildInviteBody());
      addToast({ message: t('inviteEmployee.invitationSent'), toastType: 'Success', zIndex: '9999' });
      setTimeout(() => navigate('/employees?tab=invited'), 1500);
    } catch {
      addToast({ message: t('inviteEmployee.failedToSend'), toastType: 'Error', zIndex: '9999' });
      setInviteStatus('idle');
    }
  }

  function handleBack() {
    if (currentStep > 1) {
      setCurrentStep((prev => (prev - 1) as Step));
    } else {
      navigate('/employees');
    }
  }

  function handleCancelStep1() {
    const hasInput = firstName.trim() || lastName.trim() || email.trim() || role;
    if (hasInput) {
      setShowDiscardModal(true);
    } else {
      navigate('/employees');
    }
  }

  function handleNext() {
    if (currentStep < 3) {
      setCurrentStep((prev => (prev + 1) as Step));
    }
  }

  const nextDisabled =
    currentStep === 1 ? !isStepOneValid :
      currentStep === 2 ? !isStepTwoValid :
        inviteStatus === 'loading';
  const nextLabel = currentStep === 3
    ? (inviteStatus === 'loading' ? t('inviteEmployee.sending') : t('inviteEmployee.invite'))
    : t('inviteEmployee.next');

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack} title="Back">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M12.5 15L7.5 10L12.5 5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className={styles.pageTitle}>{t('inviteEmployee.pageTitle')}</h1>
      </div>

      {/* Scrollable area below header */}
      <div className={styles.scrollArea}>
        {/* Stepper */}
        <Stepper currentStep={currentStep} />

        {/* Content card */}
        <div className={styles.content}>
          {currentStep === 1 && (
            <StepEmployee
              firstName={firstName}
              lastName={lastName}
              email={email}
              role={role}
              onFirstNameChange={setFirstName}
              onLastNameChange={setLastName}
              onEmailChange={setEmail}
              onRoleChange={setRole}
              emailError={emailError}
            />
          )}

          {currentStep === 2 && (
            <StepLocation
              locations={locations}
              isLoading={isLoadingLocations}
              error={locationsError}
              selectedIds={selectedLocationIds}
              cameraSelections={cameraSelections}
              camerasOn={camerasOn}
              onToggleLocation={handleToggleLocation}
              onCameraSelectionsChange={setCameraSelections}
              onCamerasOnChange={setCamerasOn}
              readOnly={role === 'ADMIN'}
            />
          )}

          {currentStep === 3 && (
            <>
              <StepReview
                firstName={firstName}
                lastName={lastName}
                email={email}
                roleLabel={t(`employees.roles_${role}_label`, { defaultValue: role })}
                isAdmin={role === 'ADMIN'}
                locations={locations}
                selectedLocationIds={selectedLocationIds}
                cameraSelections={cameraSelections}
                onCameraSelectionsChange={setCameraSelections}
                onEditLocations={() => setCurrentStep(2)}
                onEditUser={() => setCurrentStep(1)}
              />
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <Button
          variant="highlightTertiary"
          className={styles.footerSecondaryBtn}
          onClick={currentStep === 1 ? handleCancelStep1 : handleBack}
        >
          {currentStep === 1 ? t('inviteEmployee.cancel') : t('inviteEmployee.previous')}
        </Button>
        <Button
          variant="primary"
          className={styles.footerPrimaryBtn}
          onClick={currentStep === 3 ? handleSaveAndInvite : handleNext}
          disabled={nextDisabled}
        >
          {nextLabel}
        </Button>
      </div>

      {showDiscardModal && (
        <DiscardDraftModal
          onDiscard={() => navigate('/employees')}
          onStay={() => setShowDiscardModal(false)}
        />
      )}
    </div>
  );
}
